import { getEnergyMode } from './determine-energy-level'
import { EnergyMode } from '../../types/user-insight'

/**
 * Maps energy score (0-100) to the number of active segments (1-12)
 * based on the specified ranges:
 * 0-12: 1 part, 13-24: 2 parts, 25-34: 3 parts, etc.
 */
export const getActiveSegmentsFromScore = (score: number): number => {
  if (score <= 12) return 1
  if (score <= 24) return 2
  if (score <= 34) return 3
  if (score <= 44) return 4
  if (score <= 51) return 5
  if (score <= 58) return 6
  if (score <= 64) return 7
  if (score <= 71) return 8
  if (score <= 78) return 9
  if (score <= 84) return 10
  if (score <= 94) return 11
  return 12 // 95-100
}

/**
 * CSS variable names for energy mode colors
 * These should match the variables defined in energy-level.less
 */
const ENERGY_MODE_CSS_VARS = {
  [EnergyMode.PEAK]: '--energy-peak-primary',
  [EnergyMode.ENERGIZED]: '--energy-energized-primary',
  [EnergyMode.FUNCTIONAL]: '--energy-functional-primary',
  [EnergyMode.STRUGGLING]: '--energy-struggling-primary',
  [EnergyMode.DRAINED]: '--energy-drained-primary',
} as const

/**
 * Gets the CSS variable for an energy mode's primary color
 * Uses CSS variables from energy-level.less
 */
export const getColorForEnergyMode = (mode: EnergyMode): string => {
  const cssVar = ENERGY_MODE_CSS_VARS[mode]
  return cssVar ? `var(${cssVar})` : '#d9d9d9'
}

/**
 * Gets color based on energy score (0-100), matching the avatar color logic
 * Maps score to energy mode, then returns the corresponding CSS variable color
 */
export const getColorFromScore = (score: number): string => {
  const energyMode = getEnergyMode(score)
  return getColorForEnergyMode(energyMode)
}

/**
 * Gets color based on progress percentage (0-100), matching the avatar color logic
 * Maps progressPercent to number of filled segments (1-12),
 * then to score range, then to energy mode color
 */
export const getColorFromProgress = (progressPercent: number): string => {
  // Calculate how many segments would be filled (out of 12) based on progress
  // progressPercent represents a score (0-100), so we can use getActiveSegmentsFromScore
  const segments = getActiveSegmentsFromScore(progressPercent)

  // Map segments back to a representative score in that range
  // Use the upper bound of each range to get the correct energy mode
  let score: number
  if (segments === 1) score = 12
  else if (segments === 2) score = 24
  else if (segments === 3) score = 34
  else if (segments === 4) score = 44
  else if (segments === 5) score = 51
  else if (segments === 6) score = 58
  else if (segments === 7) score = 64
  else if (segments === 8) score = 71
  else if (segments === 9) score = 78
  else if (segments === 10) score = 84
  else if (segments === 11) score = 94
  else score = 100 // 12 segments

  // Get energy mode from score
  const energyMode = getEnergyMode(score)

  // Get color for energy mode
  return getColorForEnergyMode(energyMode)
}
